import {ExpressCheckoutElement, useElements, useStripe} from '@stripe/react-stripe-js'
import type {
  ClickResolveDetails,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'
import Big from 'big.js'
import pRetry from 'p-retry'

import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import foodGrubhubCartDb from '@/lib/food-grubhub/foodGrubhubCartDb'
import {
  confirmFoodOrder,
  createFoodGrubhubPaymentIntent,
  getFoodOrder,
} from '@/lib/food-grubhub/foodGrubhubPaymentService'
import useFoodGrubhubCartSummary from '@/lib/food-grubhub/hooks/useFoodGrubhubCartSummary'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import {
  useGetDropOffNote,
  useGetGrubhubUserAddress,
  useGetTipsAmount,
} from '@/lib/food-grubhub/store'
import {useCountry} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import useCustomerSession from '@/lib/payment/hooks/useCustomerSession'
import {useSetElementsOptions} from '@/lib/payment/PaymentProvider'
import {getRoundUp, useRoundUp} from '@/lib/payment/store'
import toStripeAmountNumber from '@/lib/payment/utils/toStripeAmountNumber'
import {FoodGrubhubCartRoute, FoodGrubhubPaymentSuccessRoute} from '@/routeRegistry'
import UnexpectedError from '@/utils/api/UnexpectedError'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import {logError} from '@/utils/logger'
import tryCatch from '@/utils/tryCatch'

interface Props {
  applyRewards: boolean
  custom?: boolean
  className?: string
  coupon?: string
  validateContactInfo: () => Promise<
    | {
        email: string
        name: string
        phone: string
      }
    | undefined
  >
}

const options: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    amazonPay: 'auto',
    applePay: 'always',
    googlePay: 'always',
    link: 'auto',
    paypal: 'auto',
  },
  layout: {
    maxColumns: 1,
    overflow: 'never',
  },
}

export default memo<Props>(function FoodGrubhubExpressCheckout({
  applyRewards,
  className,
  coupon,
  validateContactInfo,
}) {
  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()
  const latestApplyRewards = useLatest(applyRewards)
  const [country] = useCountry()
  const latestCountry = useLatest(country)
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const isAuthenticated = useIsAuthenticated()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const latestCoupon = useLatest(coupon)
  const store = useStore()
  const [roundUp] = useRoundUp()
  const latestRoundUp = useLatest(roundUp)

  const setElementsOptions = useSetElementsOptions()

  const {data: customerSession} = useCustomerSession()

  const {subtotal, deliveryFee, serviceFee, total, derivedDiscountValue, tax, roundUpAmount, cart} =
    useFoodGrubhubCartSummary(applyRewards, coupon ?? '', undefined)
  const latestPartnerCartId = useLatest(cart?.moreInfo?.partnerCartId)

  const latestSubtotal = useLatest(subtotal)
  const latestDeliveryFee = useLatest(deliveryFee)
  const latestServiceFee = useLatest(serviceFee)
  const latestDerivedDiscountValue = useLatest(derivedDiscountValue)
  const latestTax = useLatest(tax)
  const latestRoundUpAmount = useLatest(roundUpAmount)
  const getUserAddress = useGetGrubhubUserAddress(store)
  const getDropOffNote = useGetDropOffNote()
  const getTipAmount = useGetTipsAmount()
  const {data: authResponse} = useGrubhubGetAuth()
  const latestAccessToken = useLatest(authResponse?.session_handle.access_token)

  ;(function updateElementsOptions() {
    setElementsOptions(options => {
      options.mode = 'payment'
      options.setupFutureUsage = 'off_session' // vs on?
      if (customerSession) {
        options.customerSessionClientSecret = customerSession
      }
      options.amount = toStripeAmountNumber(total)
    })
  })()

  const onClick = useCallback(
    async (event: StripeExpressCheckoutElementClickEvent) => {
      const contact = await validateContactInfo()
      if (!contact) return

      const options: ClickResolveDetails = {
        allowedShippingCountries: [latestCountry.current],
        shippingAddressRequired: false,
        emailRequired: false,
        phoneNumberRequired: false,
        lineItems: [
          {
            name: 'Subtotal',
            amount: toStripeAmountNumber(latestSubtotal.current),
          },
          latestDeliveryFee.current.gt(0) && {
            name: 'Delivery fee',
            amount: toStripeAmountNumber(latestDeliveryFee.current),
          },
          latestServiceFee.current.gt(0) && {
            name: 'Service fee',
            amount: toStripeAmountNumber(latestServiceFee.current),
          },
          {
            name: 'Tax',
            amount: toStripeAmountNumber(latestTax.current),
          },
          latestRoundUp.current && {
            name: 'Donate change to NFB',
            amount: toStripeAmountNumber(latestRoundUpAmount.current),
          },
          {
            name: 'Discount',
            amount: toStripeAmountNumber(Big(-latestDerivedDiscountValue.current)),
          },
        ].filter(Boolean),
        // shippingRates: [],
      }

      console.log('click', options)

      setTimeout(() => {
        event.resolve(options)
      }, 0)
    },
    [validateContactInfo],
  )

  const onConfirm = useCallback(
    async (_event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) return

      const contact = await validateContactInfo()
      if (!contact) return

      try {
        const {error: submitError} = await elements.submit()
        if (submitError) {
          toastErrorMessage(submitError)
          console.error(submitError)
          return
        }

        // Create a ConfirmationToken using the details collected by the Express Checkout Element
        const {error: confirmationError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              allow_redisplay: 'always',
            },
          },
        })

        const confirmationTokenId = confirmationToken?.id

        if (confirmationError || !confirmationTokenId) {
          toastErrorMessage(confirmationError)
          console.error(confirmationError)
          return
        }

        const returnUrl = router.buildLocation({
          to: FoodGrubhubCartRoute.fullPath,
        }).href

        const userAddress = getUserAddress()

        const {clientSecret, order, paymentIntentStatus} = await createFoodGrubhubPaymentIntent({
          foodToken: latestAccessToken.current ?? '',
          isAuthenticated: latestIsAuthenticated.current,
          confirmationTokenId,
          applyRewards: latestApplyRewards.current,
          returnUrl,
          queryClient,
          locale: latestLocale.current,
          email: contact.email,
          coupon: latestCoupon.current,
          roundUp: getRoundUp(store),
          line1: `${userAddress.address_detail.street_number} ${userAddress.address_detail.route}`,
          line2: '',
          city: userAddress.address_detail.locality,
          state: userAddress.address_detail.administrative_area_level_1,
          postalCode: userAddress.address_detail.postal_code,
          longitude: userAddress.longitude ?? 0,
          latitude: userAddress.latitude ?? 0,
          name: contact.name,
          phone: contact.phone,
          notes: getDropOffNote(),
          driverTip: getTipAmount(),
          partnerCartId: latestPartnerCartId.current,
        })

        if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
          if (paymentIntentStatus === 'requires_action') {
            const {error: nextActionError} = await stripe.handleNextAction({clientSecret})

            if (nextActionError) {
              // eslint-disable-next-line @typescript-eslint/only-throw-error -- its okay
              throw nextActionError
            }

            await confirmFoodOrder({
              foodOrderId: order.id,
            })
          }
        }

        const [updatedOrder, errorUpdatedOrder] = await tryCatch(
          getFoodOrder({
            foodOrderId: order.id,
          }),
        )

        if (errorUpdatedOrder) {
          throw new UnexpectedError()
        }

        if (updatedOrder.status === 'AWAITING_PAYMENT') {
          await pRetry(
            () =>
              confirmFoodOrder({
                foodOrderId: order.id,
              }),
            {retries: 3},
          )
        }

        foodGrubhubCartDb.clearFoodGrubhubCart(latestCountry.current)

        await router.navigate({
          to: FoodGrubhubPaymentSuccessRoute.fullPath,
          search: {
            id: order.id,
          },
        })
      } catch (e) {
        logError(e)
        toastErrorMessage(e)
      }
    },
    [
      stripe,
      elements,
      validateContactInfo,
      router,
      getUserAddress,
      queryClient,
      store,
      getDropOffNote,
      getTipAmount,
    ],
  )

  // const onCancel = () => {
  //   console.log('canceled')
  // }

  if (total.toNumber() <= 0) {
    return null
  }

  return (
    <div className={className}>
      <ExpressCheckoutElement options={options} onConfirm={onConfirm} onClick={onClick} />
    </div>
  )
})
